import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function ManagedITServicesPage() {
  const [messageLength, setMessageLength] = useState(0);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  useEffect(() => {
    // Accordion functionality
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling as HTMLElement;
        const icon = header.querySelector('.accordion-icon');
        
        if (content.style.display === 'block') {
          content.style.display = 'none';
          icon?.classList.remove('rotate-180');
        } else {
          content.style.display = 'block';
          icon?.classList.add('rotate-180');
        }
      });
    });

    // Form character counter
    const messageTextarea = document.getElementById('message') as HTMLTextAreaElement;
    if (messageTextarea) {
      messageTextarea.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        setMessageLength(target.value.length);
        const lengthDisplay = document.getElementById('message-length');
        if (lengthDisplay) {
          lengthDisplay.textContent = target.value.length.toString();
        }
      });
    }

    // Checkbox styling
    const checkboxes = document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const checkIcon = target.nextElementSibling?.querySelector('.w-3');
        if (checkIcon) {
          if (target.checked) {
            checkIcon.classList.remove('opacity-0');
            checkIcon.classList.add('opacity-100');
          } else {
            checkIcon.classList.remove('opacity-100');
            checkIcon.classList.add('opacity-0');
          }
        }
      });
    });
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Get selected services
    const services = Array.from(formData.getAll('services')).join(', ');
    
    // Create URL encoded data
    const data = new URLSearchParams();
    data.append('name', formData.get('name') as string);
    data.append('company', formData.get('company') as string);
    data.append('email', formData.get('email') as string);
    data.append('phone', formData.get('phone') as string);
    data.append('company_size', formData.get('company_size') as string);
    data.append('services', services);
    data.append('message', formData.get('message') as string);
    data.append('terms_accepted', formData.get('terms_accepted') ? 'Yes' : 'No');

    try {
      const response = await fetch('https://readdy.ai/api/form/d50ri2l1e0me1634tfbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        alert('Thank you for your request! Our team will contact you within 24 hours.');
        form.reset();
        setMessageLength(0);
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
    }
  };

  return (
    <div className="bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative w-full">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/102a59cd0fe0f1a325b8b9630e50802c.jpg"
            alt="Managed IT Services"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <Link to="/" className="inline-flex items-center text-primary mb-4 hover:underline">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Original Page
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Managed IT Services</h1>
            <p className="text-lg text-gray-700 mb-8">
              Comprehensive IT management solutions that optimize performance, enhance security, and reduce operational costs while allowing you to focus on your core business.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Schedule a Free Consultation
              </button>
              <button className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                View Service Plans
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Managed IT Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Managed IT Services</h2>
            <p className="text-gray-600">
              Our proactive approach to IT management ensures your technology infrastructure operates at peak performance while minimizing downtime and security risks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Service Card 1 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-radar-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Proactive Monitoring</h3>
              <p className="text-gray-600 mb-4">
                24/7 monitoring of your entire IT infrastructure to identify and resolve issues before they impact your business operations.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-customer-service-2-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Help Desk Support</h3>
              <p className="text-gray-600 mb-4">
                Responsive technical support for your team with multiple communication channels and guaranteed response times.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-router-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Network Management</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive management of your network infrastructure including routers, switches, firewalls, and wireless systems.
              </p>
            </div>

            {/* Service Card 4 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Security Services</h3>
              <p className="text-gray-600 mb-4">
                Multi-layered security approach including threat detection, vulnerability management, and security policy enforcement.
              </p>
            </div>
          </div>

          {/* Second Row of Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {/* Service Card 5 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-cloud-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Cloud Management</h3>
              <p className="text-gray-600 mb-4">
                Expert management of cloud resources including migration, optimization, security, and ongoing administration.
              </p>
            </div>

            {/* Service Card 6 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-hard-drive-2-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Backup & Recovery</h3>
              <p className="text-gray-600 mb-4">
                Automated backup solutions with regular testing and rapid recovery capabilities to protect your critical data.
              </p>
            </div>

            {/* Service Card 7 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-install-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Patch Management</h3>
              <p className="text-gray-600 mb-4">
                Systematic application of updates to operating systems and applications to maintain security and stability.
              </p>
            </div>

            {/* Service Card 8 */}
            <div className="service-card bg-white rounded-lg shadow-md p-6 transition-transform hover:shadow-lg hover:-translate-y-1 border border-gray-100">
              <div className="service-icon w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-file-chart-line text-2xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">IT Documentation</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive documentation of your IT environment including network diagrams, asset inventory, and procedures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Commitments & Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Service Commitments */}
            <div>
              <span className="text-primary font-semibold mb-2 block">SERVICE LEVEL AGREEMENTS</span>
              <h2 className="text-3xl font-bold mb-6">Our Service Commitments</h2>
              <p className="text-gray-600 mb-8">
                We back our managed IT services with industry-leading service level agreements that guarantee performance, availability, and response times.
              </p>
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">System Uptime</h3>
                    <span className="text-primary font-bold">99.9%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: '99.9%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    We guarantee 99.9% uptime for critical systems with financial penalties if we fail to deliver.
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">Response Time</h3>
                    <span className="text-primary font-bold">15-120 min</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-red-500 h-2.5 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Critical: 15 min</p>
                    </div>
                    <div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-yellow-500 h-2.5 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">High: 30 min</p>
                    </div>
                    <div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Normal: 120 min</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Guaranteed response times based on issue severity, with escalation procedures for all service requests.
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">Resolution Time</h3>
                    <span className="text-primary font-bold">4-24 hours</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div className="bg-primary h-2.5 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600">
                    85% of all issues resolved within the first 4 hours, with 98% resolved within 24 hours.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Why Choose */}
            <div>
              <span className="text-primary font-semibold mb-2 block">KEY BENEFITS</span>
              <h2 className="text-3xl font-bold mb-6">Why Choose Our Managed IT Services</h2>
              <p className="text-gray-600 mb-8">
                Our managed IT services deliver measurable business value through improved performance, enhanced security, and reduced operational costs.
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-money-dollar-circle-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Predictable IT Costs</h3>
                    <p className="text-gray-600">
                      Convert unpredictable IT expenses into fixed monthly costs, making budgeting easier and eliminating surprise expenditures.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Enhanced Security</h3>
                    <p className="text-gray-600">
                      Proactive security measures that protect your business from evolving threats, with continuous monitoring and rapid incident response.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-speed-up-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Improved Productivity</h3>
                    <p className="text-gray-600">
                      Minimize downtime and technical issues that impact employee productivity, with fast resolution times and proactive problem prevention.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-team-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Expert IT Team</h3>
                    <p className="text-gray-600">
                      Access to a diverse team of certified IT professionals with specialized expertise across multiple technology domains.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <div className="w-6 h-6 flex items-center justify-center text-primary">
                      <i className="ri-focus-3-line text-xl"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Focus on Core Business</h3>
                    <p className="text-gray-600">
                      Free your internal resources to focus on strategic initiatives that drive business growth rather than day-to-day IT management.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Measurable Performance Improvements */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">Measurable Performance Improvements</h2>
            <p className="text-gray-600">
              Our managed IT services deliver quantifiable results that directly impact your business operations and bottom line.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Reduced Downtime</h3>
                <span className="text-primary font-bold text-2xl">92%</span>
              </div>
              <p className="text-gray-600">
                Average reduction in system downtime after implementing our managed IT services.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Cost Savings</h3>
                <span className="text-primary font-bold text-2xl">37%</span>
              </div>
              <p className="text-gray-600">
                Average reduction in overall IT costs compared to maintaining an equivalent in-house IT department.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Security Incidents</h3>
                <span className="text-primary font-bold text-2xl">85%</span>
              </div>
              <p className="text-gray-600">
                Reduction in security incidents after implementing our managed security services.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">IT Ticket Resolution</h3>
                <span className="text-primary font-bold text-2xl">4.2h</span>
              </div>
              <p className="text-gray-600">
                Average time to resolution for all IT support tickets, compared to industry average of 24 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features & Capabilities */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4">Technical Features & Capabilities</h2>
            <p className="text-gray-600">
              Explore the advanced technical features that power our managed IT services and deliver exceptional performance.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {/* Accordion 1 - Monitoring Capabilities */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button className="accordion-header w-full flex justify-between items-center p-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center text-primary mr-3">
                      <i className="ri-radar-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800">Monitoring Capabilities</h3>
                  </div>
                  <div className="accordion-icon w-6 h-6 flex items-center justify-center text-gray-400 transform transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                <div className="accordion-content px-5 pb-5" style={{ display: 'none' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Infrastructure Monitoring</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Server performance & health monitoring</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Network device monitoring (switches, routers, firewalls)</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Storage system monitoring & capacity planning</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Virtualization platform monitoring</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Application & Service Monitoring</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Business application availability monitoring</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Database performance monitoring</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Website & web application monitoring</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">End-user experience monitoring</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 2 - Security Features */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button className="accordion-header w-full flex justify-between items-center p-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center text-primary mr-3">
                      <i className="ri-shield-keyhole-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800">Security Features</h3>
                  </div>
                  <div className="accordion-icon w-6 h-6 flex items-center justify-center text-gray-400 transform transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                <div className="accordion-content px-5 pb-5" style={{ display: 'none' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Endpoint Security</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Next-generation antivirus protection</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Endpoint detection & response (EDR)</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Device encryption management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Application control & whitelisting</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Network Security</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Next-generation firewall management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Intrusion detection & prevention</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">VPN management & secure remote access</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Network traffic analysis & anomaly detection</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 3 - Backup & Recovery Solutions */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button className="accordion-header w-full flex justify-between items-center p-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center text-primary mr-3">
                      <i className="ri-hard-drive-2-line text-2xl"></i>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800">Backup & Recovery Solutions</h3>
                  </div>
                  <div className="accordion-icon w-6 h-6 flex items-center justify-center text-gray-400 transform transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                <div className="accordion-content px-5 pb-5" style={{ display: 'none' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Backup Technologies</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Image-based backup solutions</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">File-level backup & recovery</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Cloud backup solutions</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Database backup & recovery</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Disaster Recovery</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Business continuity planning</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Disaster recovery testing</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Rapid recovery capabilities</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Offsite replication & recovery</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 4 - Cloud Management */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button className="accordion-header w-full flex justify-between items-center p-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center text-primary mr-3">
                      <i className="ri-cloud-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800">Cloud Management</h3>
                  </div>
                  <div className="accordion-icon w-6 h-6 flex items-center justify-center text-gray-400 transform transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                <div className="accordion-content px-5 pb-5" style={{ display: 'none' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Cloud Platforms</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Microsoft Azure management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Amazon Web Services (AWS) management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Google Cloud Platform management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Private cloud infrastructure management</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Cloud Services</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Cloud migration services</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Cloud cost optimization</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Hybrid cloud management</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Cloud security & compliance</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accordion 5 - Compliance & Reporting */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button className="accordion-header w-full flex justify-between items-center p-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center text-primary mr-3">
                      <i className="ri-file-chart-line text-xl"></i>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-800">Compliance & Reporting</h3>
                  </div>
                  <div className="accordion-icon w-6 h-6 flex items-center justify-center text-gray-400 transform transition-transform duration-300">
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </button>
                <div className="accordion-content px-5 pb-5" style={{ display: 'none' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Compliance Support</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">HIPAA compliance for healthcare</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">PCI DSS compliance for payment processing</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">GDPR compliance for data protection</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">SOC 2 compliance for service organizations</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Reporting Capabilities</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Executive summary reports</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Detailed technical reports</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Compliance audit reports</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                            <i className="ri-check-line"></i>
                          </div>
                          <span className="text-gray-600">Real-time reporting dashboards</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Transform CTA & Contact Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left Column - CTA */}
                <div className="p-8 lg:p-12 bg-primary text-white">
                  <h2 className="text-3xl font-bold mb-6">Ready to Transform Your IT Operations?</h2>
                  <p className="mb-8">
                    Schedule a consultation with our managed services experts to discuss your specific needs and how we can help you achieve your business objectives.
                  </p>
                  <div className="space-y-6 mb-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Free IT Assessment</h3>
                        <p className="text-blue-100">
                          Comprehensive evaluation of your current IT infrastructure and security posture.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Customized Service Plan</h3>
                        <p className="text-blue-100">
                          Tailored managed IT solutions designed for your specific industry and requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center text-white">
                          <i className="ri-check-line"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Transparent Pricing</h3>
                        <p className="text-blue-100">
                          Clear, predictable pricing with no hidden fees or unexpected charges.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Contact Form */}
                <div className="p-8 lg:p-12">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">Request a Managed IT Services Consultation</h3>
                  <form id="contact-form" data-readdy-form action="https://readdy.ai/api/form/d50ri2l1e0me1634tfbg" method="post" onSubmit={handleFormSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Name</label>
                        <input 
                          id="name" 
                          name="name" 
                          required 
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-gray-700 font-medium mb-2">Company</label>
                        <input 
                          id="company" 
                          name="company" 
                          required 
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                          placeholder="Your company"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email</label>
                        <input 
                          type="email" 
                          id="email" 
                          name="email" 
                          required 
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                          placeholder="Your email"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Phone</label>
                        <input 
                          type="tel" 
                          id="phone" 
                          name="phone" 
                          required 
                          className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                          placeholder="Your phone number"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="company-size" className="block text-gray-700 font-medium mb-2">Company Size</label>
                      <select 
                        id="company-size" 
                        name="company_size" 
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-8"
                      >
                        <option value="">Select company size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label className="block text-gray-700 font-medium mb-2">Services of Interest</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="monitoring" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Proactive Monitoring</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="helpdesk" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Help Desk Support</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="security" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Security Services</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="cloud" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Cloud Management</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="backup" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Backup & Recovery</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="hidden peer" name="services" value="network" />
                          <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                            <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                          <span className="text-sm text-gray-700">Network Management</span>
                        </label>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Message</label>
                      <textarea 
                        id="message" 
                        name="message" 
                        rows={4} 
                        maxLength={500} 
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
                        placeholder="Tell us about your current IT challenges and requirements"
                      ></textarea>
                      <div className="text-sm text-gray-500 mt-1">
                        <span id="message-length">0</span>/500 characters
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="flex items-center cursor-pointer">
                        <input type="checkbox" name="terms_accepted" required className="hidden peer" />
                        <div className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center mr-2 peer-checked:bg-primary peer-checked:border-primary">
                          <div className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <span className="text-sm text-gray-600">I agree to Thyamis's privacy policy and terms of service</span>
                      </label>
                    </div>

                    <button 
                      type="submit"
                      className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium w-full hover:bg-primary/90 transition-colors cursor-pointer"
                    >
                      Submit Request
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}